import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import "./EmailVerification.scss";
import { verifyRecipientEmail } from "../../services/v5Api";
import successTick from "../../assets/images/successTick.svg";
import videoUploadError from "../../assets/images/videoUploadError.svg";

const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  EXPIRED: "expired",
  INVALID: "invalid",
  ERROR: "error",
};

const LOGO_URL =
  "https://hma-dev-assets.s3.ap-northeast-1.amazonaws.com/logo/sllogo148.png";

const ExpiredIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="email-verification__icon">
    <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" />
    <path
      d="M12 7v5l3 3"
      stroke="#F59E0B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmailVerification = ({ token }) => {
  const [status, setStatus] = useState(STATUS.LOADING);
  const [resendRequested, setResendRequested] = useState(false);

  useEffect(() => {
    verifyRecipientEmail(token)
      .then(() => setStatus(STATUS.SUCCESS))
      .catch((err) => {
        const message = err.response?.data?.message || "";
        if (message.toLowerCase().includes("expired")) {
          setStatus(STATUS.EXPIRED);
        } else if (err.response?.status === 404) {
          setStatus(STATUS.INVALID);
        } else if (err.response?.status === 401) {
          setStatus(STATUS.INVALID);
        } else {
          setStatus(STATUS.ERROR);
        }
      });
  }, [token]);

  const renderContent = () => {
    if (status === STATUS.LOADING) {
      return (
        <Box sx={{ py: 4 }}>
          <CircularProgress
            size={48}
            sx={{ color: "var(--primary-color, #1976d2)" }}
          />
          <Typography className="email-verification__body" sx={{ mt: 3 }}>
            Verifying your email address…
          </Typography>
        </Box>
      );
    }

    if (status === STATUS.SUCCESS) {
      return (
        <>
          <div className="email-verification__icon-wrap email-verification__icon-wrap--success">
            <img
              src={successTick}
              alt="Success"
              className="email-verification__icon"
            />
          </div>
          <Typography className="email-verification__title">
            Email verified!
          </Typography>
          <Typography className="email-verification__body">
            Your email address has been confirmed. You will start receiving
            SeniorLife reports at this address.
          </Typography>
          <div className="email-verification__divider" />
          <Typography className="email-verification__hint">
            You can close this tab.
          </Typography>
        </>
      );
    }

    if (status === STATUS.EXPIRED) {
      return (
        <>
          <div className="email-verification__icon-wrap email-verification__icon-wrap--expired">
            <ExpiredIcon />
          </div>
          <Typography className="email-verification__title">
            Link expired
          </Typography>
          <Typography className="email-verification__body">
            This verification link is more than 7 days old and is no longer
            valid.
          </Typography>
          {!resendRequested ? (
            <Button
              variant="contained"
              onClick={() => setResendRequested(true)}
              sx={{
                backgroundColor: "var(--primary-color, #1976d2)",
                borderRadius: "8px",
                textTransform: "none",
                px: 4,
                py: 1.5,
                fontSize: "15px",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "var(--primary-color, #1565c0)",
                  filter: "brightness(0.9)",
                },
              }}
            >
              Request a new link
            </Button>
          ) : (
            <>
              <div className="email-verification__divider" />
              <Typography className="email-verification__hint">
                Please contact the person who invited you and ask them to resend
                the verification email from their SeniorLife dashboard.
              </Typography>
            </>
          )}
        </>
      );
    }

    if (status === STATUS.INVALID) {
      return (
        <>
          <div className="email-verification__icon-wrap email-verification__icon-wrap--error">
            <img
              src={videoUploadError}
              alt="Error"
              className="email-verification__icon"
            />
          </div>
          <Typography className="email-verification__title">
            Invalid link
          </Typography>
          <Typography className="email-verification__body">
            This verification link is invalid or has already been used. If you
            believe this is an error, please contact the person who invited you.
          </Typography>
        </>
      );
    }

    return (
      <>
        <div className="email-verification__icon-wrap email-verification__icon-wrap--error">
          <img
            src={videoUploadError}
            alt="Error"
            className="email-verification__icon"
          />
        </div>
        <Typography className="email-verification__title">
          Something went wrong
        </Typography>
        <Typography className="email-verification__body">
          We were unable to verify your email right now. Please try again later
          or contact support.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            px: 4,
            py: 1.5,
            fontSize: "15px",
          }}
        >
          Try again
        </Button>
      </>
    );
  };

  return (
    <div className="email-verification">
      <div className="email-verification__card">
        <img
          src={LOGO_URL}
          alt="SeniorLife"
          className="email-verification__logo"
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default EmailVerification;
